// store/slices/cart/slice.js
import { createSlice, createSelector } from "@reduxjs/toolkit";

// Funkcije za localStorage
const loadCartFromStorage = () => {
  try {
    const serializedCart = localStorage.getItem("cart");
    if (serializedCart === null) {
      return {
        items: [],
        totalItems: 0,
        totalPrice: 0,
      };
    }
    return JSON.parse(serializedCart);
  } catch (err) {
    console.error("Error loading cart from localStorage:", err);
    return {
      items: [],
      totalItems: 0,
      totalPrice: 0,
    };
  }
};

const saveCartToStorage = (cart) => {
  try {
    const serializedCart = JSON.stringify(cart);
    localStorage.setItem("cart", serializedCart);
  } catch (err) {
    console.error("Error saving cart to localStorage:", err);
  }
};

const calculateTotals = (items) => {
  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);
  const totalPrice = items.reduce(
    (sum, item) => sum + (item.salePrice || item.price) * item.quantity,
    0
  );

  return { totalItems, totalPrice };
};

const initialState = loadCartFromStorage();

export const cartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    addToCart: (state, action) => {
      const { product, quantity = 1, selectedSize = null } = action.payload;

      // Kreiraj jedinstveni ID za cart item (product ID + size)
      const cartItemId = selectedSize
        ? `${product.id}_${selectedSize}`
        : product.id.toString();

      // Pronađi postojeći item
      const existingItem = state.items.find(
        (item) => item.cartItemId === cartItemId
      );

      if (existingItem) {
        // Ažuriraj količinu postojećeg itema
        existingItem.quantity += quantity;
      } else {
        // Dodaj novi item
        const cartItem = {
          cartItemId,
          id: product.id,
          name: product.name,
          price: product.price,
          salePrice: product.salePrice,
          selectedSize,
          quantity,
          band: product.band,
          category: product.category,
          mainImage: product.mainImage || product.productImages?.[0]?.url,
          stockQuantity: product.stockQuantity,
        };
        state.items.push(cartItem);
      }

      // Preračunaj totale
      const totals = calculateTotals(state.items);
      state.totalItems = totals.totalItems;
      state.totalPrice = totals.totalPrice;

      // Sačuvaj u localStorage
      saveCartToStorage(state);
    },

    removeFromCart: (state, action) => {
      const cartItemId = action.payload;
      state.items = state.items.filter(
        (item) => item.cartItemId !== cartItemId
      );

      // Preračunaj totale
      const totals = calculateTotals(state.items);
      state.totalItems = totals.totalItems;
      state.totalPrice = totals.totalPrice;

      // Sačuvaj u localStorage
      saveCartToStorage(state);
    },

    updateQuantity: (state, action) => {
      const { cartItemId, quantity } = action.payload;
      const item = state.items.find((item) => item.cartItemId === cartItemId);

      if (item) {
        if (quantity <= 0) {
          // Ukloni item ako je količina 0 ili manje
          state.items = state.items.filter(
            (item) => item.cartItemId !== cartItemId
          );
        } else {
          // Ažuriraj količinu
          item.quantity = quantity;
        }

        // Preračunaj totale
        const totals = calculateTotals(state.items);
        state.totalItems = totals.totalItems;
        state.totalPrice = totals.totalPrice;

        // Sačuvaj u localStorage
        saveCartToStorage(state);
      }
    },

    clearCart: (state) => {
      state.items = [];
      state.totalItems = 0;
      state.totalPrice = 0;

      // Očisti localStorage
      localStorage.removeItem("cart");
    },

    // Ažuriraj cene proizvodem (korisno kad se cene promene)
    syncCartWithProducts: (state, action) => {
      const products = action.payload;

      state.items = state.items.map((cartItem) => {
        const product = products.find((p) => p.id === cartItem.id);
        if (product) {
          return {
            ...cartItem,
            price: product.price,
            salePrice: product.salePrice,
            stockQuantity: product.stockQuantity,
          };
        }
        return cartItem;
      });

      // Preračunaj totale
      const totals = calculateTotals(state.items);
      state.totalItems = totals.totalItems;
      state.totalPrice = totals.totalPrice;

      // Sačuvaj u localStorage
      saveCartToStorage(state);
    },
  },
});

export const {
  addToCart,
  removeFromCart,
  updateQuantity,
  clearCart,
  syncCartWithProducts,
} = cartSlice.actions;

// Selectors
export const selectCart = (state) => state.cart;
export const selectCartItems = (state) => state.cart.items;

// Memoized selector for cart totals to prevent unnecessary re-renders
export const selectCartTotals = createSelector(
  [selectCart],
  (cart) => ({
    totalItems: cart.totalItems,
    totalPrice: cart.totalPrice,
  })
);

export default cartSlice.reducer;
